import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Globe, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { LOGO_URL, PHONES } from "@/lib/brand";

const NavLink = ({ href, children, onClick, testId }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={testId}
    className="text-sm font-medium text-zinc-300 hover:text-white transition-colors relative group"
  >
    {children}
    <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#EC008C] transition-all duration-300 group-hover:w-full" />
  </a>
);

export default function Navbar() {
  const { lang, t, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#services", label: t.nav.services, id: "nav-services" },
    { href: "#pricing", label: t.nav.pricing, id: "nav-pricing" },
    { href: "#how", label: t.nav.how, id: "nav-how" },
    { href: "#areas", label: t.nav.areas, id: "nav-areas" },
    { href: "#faq", label: t.nav.faq, id: "nav-faq" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="h-20 flex items-center justify-between">
          <a href="#top" data-testid="logo-link" className="flex items-center gap-3 group">
            <div className="relative w-12 h-12 rounded-xl overflow-hidden ring-1 ring-white/10 bg-black flex items-center justify-center">
              <img src={LOGO_URL} alt="Rosas Cleaning Services" className="w-full h-full object-cover scale-[1.6]" />
              <Sparkles className="absolute -top-1 -right-1 w-3.5 h-3.5 text-[#FF1F87] sparkle" />
            </div>
            <div className="hidden sm:block leading-none">
              <div className="font-display font-black text-white text-base tracking-tight">
                ROSAS
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">
                Cleaning Services
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink key={l.id} href={l.href} testId={l.id}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="hidden md:flex items-center gap-1.5 text-xs font-semibold text-zinc-300 hover:text-white border border-white/15 hover:border-[#EC008C] rounded-full px-3 py-2 transition-all"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === "es" ? "ES" : "EN"}
            </button>

            <a
              href={`tel:+1${PHONES[0].replace(/-/g, "")}`}
              data-testid="nav-call"
              className="hidden sm:inline-flex items-center gap-2 text-xs font-semibold text-white hover:text-[#FF1F87] border border-white/15 hover:border-[#EC008C] rounded-full px-3 py-2 transition-all"
            >
              <Phone className="w-3.5 h-3.5" />
              {PHONES[0]}
            </a>

            <a
              href="#book"
              data-testid="nav-book-cta"
              className="hidden md:inline-flex bg-[#EC008C] hover:bg-[#FF1F87] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(236,0,140,0.4)]"
            >
              {t.nav.book}
            </a>

            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white p-2"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  data-testid={`mobile-${l.id}`}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-zinc-200 hover:text-[#FF1F87]"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex items-center gap-3 pt-3 border-t border-white/10">
                <button
                  data-testid="mobile-lang-toggle"
                  onClick={() => setLang(lang === "es" ? "en" : "es")}
                  className="flex items-center gap-1.5 text-xs font-semibold text-zinc-300 border border-white/15 rounded-full px-3 py-2"
                >
                  <Globe className="w-3.5 h-3.5" /> {lang === "es" ? "ES" : "EN"}
                </button>
                <a
                  href="#book"
                  data-testid="mobile-book-cta"
                  onClick={() => setOpen(false)}
                  className="flex-1 text-center bg-[#EC008C] text-white text-sm font-semibold px-5 py-2.5 rounded-full"
                >
                  {t.nav.book}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
